// The University of Tulsa: public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Tulsa posts no Common Data Set: its Office of Institutional Research
//    and Data Analytics page offers student outcome reports and a data
//    request form, and a search of utulsa.edu for "common data set" finds
//    nothing else (checked 2026-10-01). The first-year counts come from the
//    U.S. Department of Education's IPEDS Admissions survey for Fall 2024,
//    first-time degree-seeking students (College Navigator and the ADM2024
//    complete data file, unit 207971).
//  - Tulsa Admission, First-Year Domestic Students: rolling admission, the
//    Important Dates list (priority scholarship December 15, special
//    programs February 1, National Merit Semifinalists November 1), the test
//    optional policy (scores not required for fall 2027 entry), and the
//    announcement that tuition and university fees are $25,000 a year,
//    guaranteed for four years, starting with the fall 2027 entering class.
//    Tulsa publishes no decision dates and has no early decision or early
//    action plan. The site served these pages only to a browser, so they
//    were read with Playwright.
//  - Tulsa Admission, Special Programs (each program applies separately).
//  - Tulsa Tuition & Aid, Federal Cost of Attendance 2026-27, new first-year
//    students, full-time, on campus (its total, $80,668, equals the sum of
//    its items). Quirk: these are 2026-27 prices; students entering in fall
//    2027 fall under the announced $25,000 tuition and fees, which Tulsa has
//    not yet posted as a 2027-28 cost of attendance. The note says so.
//  - College Scorecard, school id 207971 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=207971#admsns',
        read: '2026-10-01',
    },
    research: {
        label: 'Tulsa Office of Institutional Research and Data Analytics',
        url: 'https://utulsa.edu/about/offices/institutional-research-data-analytics/',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Tulsa Admission, First-Year Domestic Students',
        url: 'https://utulsa.edu/admissions/apply/first-year-domestic-students/',
        read: '2026-10-01',
    },
    special: {
        label: 'Tulsa Admission, Special Programs',
        url: 'https://utulsa.edu/admissions/undergraduate-admission/special-programs/',
        read: '2026-10-01',
    },
    costs: {
        label: 'Tulsa Tuition & Aid, Federal Cost of Attendance 2026-27',
        url: 'https://utulsa.edu/tuition-aid/tuition-costs/federal-cost-of-attendance-2026-27/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?207971-University-of-Tulsa',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-tulsa',
    kbId: 'university_of_tulsa',
    name: 'University of Tulsa',
    shortName: 'Tulsa',
    aliases: ['TU'],
    location: 'Tulsa, OK',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Tulsa Athletics rebranding release); decoration only.
    branding: {
        primary: '#003595',
        secondary: '#D0B787',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_tulsa_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 5121,
        admitted: 3150,
        enrolled: 733,
        source: 'ipeds',
        where: 'first-time, degree-seeking undergraduates',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Tulsa posts no Common Data Set and publishes no first-year acceptance rate by college or major; the counts above are the federal IPEDS figures for the whole university.',
        source: 'research',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Tulsa once, through the UTulsa Application or the Common App. Special programs ask for more:',
        rows: [
            { school: 'Honors College, TU Scholars and other special programs', requirement: 'Each program has its own requirements and its own application. Special program applications are due February 1.', source: 'special' },
            { school: 'National Merit Semifinalists', requirement: 'To be eligible for the National Merit Semifinalist package, apply by November 1 and visit campus for an in-person interview by November 1.', source: 'firstYear' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Admission', value: 'Rolling' },
        rows: [
            { plan: 'Rolling admission', deadline: 'Apply any time; applications are read as they arrive', decision: null, binding: false },
            { plan: 'National Merit Semifinalist deadline', deadline: 'November 1', decision: null, binding: false },
            { plan: 'Priority scholarship deadline', deadline: 'December 15', decision: null, binding: false },
            { plan: 'Special programs deadline', deadline: 'February 1', decision: null, binding: false },
        ],
        note: 'Tulsa has no early decision or early action plan and does not publish decision dates. To be considered for scholarships, you must be admitted before August 1 for fall entry. The UTulsa Application opens July 7 and the Common App August 1.',
        source: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Tulsa does not require the ACT or SAT from first-year students applying for fall 2027 entry. If you send scores, Tulsa reads them, and it also uses them in awarding scholarships. You may self-report your highest scores; official scores are required only if you enroll. The ACT science section is optional.',
        source: 'firstYear',
    },

    // Announced changes already in effect for this cycle's applicants.
    currentChanges: [
        { topic: 'Tuition', line: 'Starting with the fall 2027 entering class, Tulsa\'s undergraduate tuition and university fees are $25,000 a year, guaranteed at that rate for four years.', source: 'firstYear' },
    ],

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 52790 },
            { item: 'Fees', amount: 1320 },
            { item: 'Student service fee', amount: 562 },
            { item: 'Food and housing (on campus)', amount: 17306 },
            { item: 'Books and supplies', amount: 600 },
            { item: 'Travel', amount: 6120 },
            { item: 'Personal expenses', amount: 1890 },
            { item: 'Stafford loan fees', amount: 80 },
        ],
        total: 80668,
        note: 'For a new full-time first-year student living on campus in 2026-27. Students entering in fall 2027 pay the announced $25,000 a year in tuition and university fees instead of the tuition and fee lines here; Tulsa has not yet posted a 2027-28 cost of attendance.',
        source: 'costs',
        netPrice: {
            average: 13511,
            byIncome: [
                { income: '$0 to $30,000', amount: 9403 },
                { income: '$30,001 to $48,000', amount: 3599 },
                { income: '$48,001 to $75,000', amount: 8648 },
                { income: '$75,001 to $110,000', amount: 15085 },
                { income: 'Over $110,000', amount: 25486 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Tulsa\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
